import { locationMst } from '@libs/domain';
import type { FieldErrorField } from '@ps-smart-cp/ppfw-client';
import {
  CheckboxField,
  FieldSet,
  TextNumberField,
  TextStringField,
  kebabCase,
  useItemName,
  useValidator,
} from '@ps-smart-cp/ppfw-client';
import type { Ref } from 'react';
import { forwardRef, useImperativeHandle, useState } from 'react';
import { useMstSettings, useOperationControl } from '../../../context';
import { locationMstSettingFieldConfig, locationMstSettingFieldConfigForCheck } from '../item-config';
import { registerSetting } from '../repositories';

interface LocationSettingFieldSetProps {
  errorFields?: string[];
}

interface LocationSettingFieldSetRef {
  validate: () => FieldErrorField[];
  register: () => Promise<void>;
}

function InnerComponent(props: LocationSettingFieldSetProps, ref: Ref<LocationSettingFieldSetRef>) {
  const getName = useItemName();
  const { location } = useMstSettings();
  const [setting, setSetting] = useState(location);
  const validator = useValidator();
  const validate = () =>
    setting.isUsed
      ? validator
          .with(setting, locationMstSettingFieldConfigForCheck)
          .execute()
          .map((s) => ({ ...s, field: kebabCase(`location-${s.field}`) }))
      : [];
  const register = async () => registerSetting(setting, getName(locationMst.names.location));
  const { isStarted } = useOperationControl();

  useImperativeHandle(ref, () => ({ validate, register }), [setting]);

  return (
    <FieldSet legendProps={{ title: getName(locationMst.names.location), id: 'location-field-set' }}>
      <CheckboxField
        id="location-is-used"
        obj={setting}
        setObj={setSetting}
        prop="isUsed"
        items={locationMstSettingFieldConfig}
        errorFields={props.errorFields}
        labelName=""
        disabled={isStarted}
      />
      <TextStringField
        id="location-display-name"
        obj={setting}
        setObj={setSetting}
        prop="displayName"
        items={locationMstSettingFieldConfig}
        errorFields={props.errorFields}
        disabled={isStarted ? true : !setting.isUsed}
      />
      <TextNumberField
        id="location-code-digits"
        obj={setting}
        setObj={setSetting}
        prop="codeDigits"
        items={locationMstSettingFieldConfig}
        errorFields={props.errorFields}
        disabled={isStarted ? true : !setting.isUsed}
      />
    </FieldSet>
  );
}

export const LocationSettingFieldSet = forwardRef(InnerComponent);
export type { LocationSettingFieldSetRef };
